import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useDelivery } from 'api/hooks/useDelivery';
import { useSaveAllTrayInspections } from 'api/hooks/useSaveAllTrayInspections';
import { useUpdateSetInspection } from 'api/hooks/useUpdateSetInspection';
import { useUpdateTrayInspection } from 'api/hooks/useUpdateTrayInspection';
import { QueryKeys } from 'api/models/QueryKeys';
import { TrayImagePathParams, TrayInspectionInfo } from 'api/models/Trays';
import { useLanguage } from 'context/AppContext';
import { useCheckInTraysBySet, CheckInSet } from 'hooks/useCheckInTraysBySet';
import useGoogleStorageHelpers from 'utils/helpers/googleStorage.helpers';

export const useSaveCheckInTrayInspectionsForSet = () => {
  const lang = useLanguage()[0].CheckInTrayDetailsPage;
  const { deliveryId = '' } = useParams();

  const { data: delivery, refetch: fetchDelivery } = useDelivery(deliveryId);
  const { data: currentUser } = useCurrentUser();
  const [allCheckInStates, setCheckInTraysBySet] = useCheckInTraysBySet();

  const [isLoading, setIsLoading] = useState(false);

  // This is where the upload helpers are brought in
  const { uploadImage, constructTrayImagePath } = useGoogleStorageHelpers();
  const { mutateAsync: saveAllTrayInspections } = useSaveAllTrayInspections();
  const { mutateAsync: updateTrayInspection } = useUpdateTrayInspection();
  const { mutateAsync: updateSetInspection } = useUpdateSetInspection();
  const queryClient = useQueryClient();

  const saveCheckInTrayInspectionsForSet = useCallback(
    async ({
      setKitInstanceId,
      checkInSet,
      skipInspection = false,
    }: {
      setKitInstanceId: string;
      checkInSet: CheckInSet;
      skipInspection?: boolean;
    }) => {
      if (!delivery || !currentUser) {
        throw new Error('Delivery or User not found');
      }

      setIsLoading(true);

      try {
        // 1. Gather all existing upload promises from the state
        const initialSetState = allCheckInStates[setKitInstanceId];
        if (!initialSetState) throw new Error('Set state not found.');

        const backgroundPromises: Promise<void>[] = [];
        for (const trayId in initialSetState.trays) {
          const tray = initialSetState.trays[trayId];
          if (tray.trayImage?.uploadPromise) {
            backgroundPromises.push(tray.trayImage.uploadPromise);
          }
          tray.decantedImages?.forEach(img => {
            if (img.uploadPromise) {
              backgroundPromises.push(img.uploadPromise);
            }
          });
        }

        // 2. Wait for all background uploads to settle
        await Promise.allSettled(backgroundPromises);

        // 3. Re-read state and perform fallback uploads for any images that failed
        const stateAfterBackgroundUploads = allCheckInStates[setKitInstanceId];
        if (!stateAfterBackgroundUploads) throw new Error('Set state disappeared unexpectedly.');

        const fallbackPromises: Promise<void>[] = [];
        for (const trayId in stateAfterBackgroundUploads.trays) {
          const tray = stateAfterBackgroundUploads.trays[trayId];
          const pathParams: Omit<TrayImagePathParams, 'decantedTrayId'> = {
            stage: 'CHECK_IN',
            deliveryId,
            setId: setKitInstanceId,
            trayId,
          };

          // Fallback for the main tray image
          if (tray.trayImage && !tray.trayImage.gsImagePath) {
            const { captureId, dataUri } = tray.trayImage;
            if (!dataUri) throw new Error(`Tray image data uri is missing for tray ${tray.name}`);

            const promise = (async () => {
              const imagePath = constructTrayImagePath(pathParams);
              const gsImagePath = await uploadImage(imagePath, dataUri);
              setCheckInTraysBySet(prev => {
                const next = { ...prev };
                const t = next[setKitInstanceId]?.trays[trayId];
                if (t?.trayImage?.captureId === captureId) {
                  t.trayImage.gsImagePath = gsImagePath;
                }
                return next;
              });
            })();

            fallbackPromises.push(promise);
          }

          // Fallback for decanted images
          tray.decantedImages?.forEach(image => {
            if (!image.gsImagePath) {
              const { captureId, dataUri } = image;
              const promise = (async () => {
                if (!dataUri) throw new Error(`Decanted image data uri is missing for tray ${tray.name}`);
                const decantPathParams: TrayImagePathParams = { ...pathParams, decantedTrayId: captureId };
                const imagePath = constructTrayImagePath(decantPathParams);
                const gsImagePath = await uploadImage(imagePath, dataUri);
                setCheckInTraysBySet(prev => {
                  const next = { ...prev };
                  const img = next[setKitInstanceId]?.trays[trayId]?.decantedImages?.find(
                    i => i.captureId === captureId,
                  );
                  if (img) {
                    img.gsImagePath = gsImagePath;
                  }
                  return next;
                });
              })();
              fallbackPromises.push(promise);
            }
          });
        }

        // Await all fallback uploads
        await Promise.all(fallbackPromises);

        // 4. Build the final payload
        const finalSetState = allCheckInStates[setKitInstanceId];
        if (!finalSetState) throw new Error('Set state disappeared unexpectedly after uploads.');

        const allTraysInspection = Object.entries(finalSetState.trays).map(([trayId, liveTrayState]) => {
          const trayFromParam = checkInSet.trays[trayId]; // Get metadata from the param
          const trayImagePath = liveTrayState?.trayImage?.gsImagePath;

          // After all fallbacks, the image path must exist.
          if (!trayImagePath && !skipInspection) {
            throw new Error(`Missing tray image for tray ${liveTrayState.name} after all upload attempts.`);
          }

          const finalDecantedTrays =
            trayFromParam?.decantedTrays?.map((t, index) => {
              const imagePath = liveTrayState?.decantedImages?.[index]?.gsImagePath;
              if (!imagePath && !skipInspection) {
                // This check is critical. After all fallbacks, the image path must exist.
                throw new Error(
                  `Missing decanted image for tray ${liveTrayState.name} at index ${index} after all upload attempts.`,
                );
              }
              return {
                trayReference: t?.trayReference ?? null,
                assetNumber: t?.assetNumber,
                gsImagePath: imagePath,
              };
            }) || [];

          return {
            trayInstanceId: trayFromParam.trayInstanceId || null,
            trayName: trayFromParam.name,
            trayReference: trayFromParam.trayReference ?? null,
            assetNumber: trayFromParam.assetNumber,
            decantedTrays: finalDecantedTrays,
            gsImagePath: trayImagePath || '',
            trayInspection: {
              allClean: Boolean(trayFromParam.allDevicesCleanOfDebris),
              allFunctional: Boolean(trayFromParam.allDevicesFunctional),
              allPartsPresent: Boolean(trayFromParam.allDevicesInTray),
              trayUsed: false,
              missingParts: [],
              missingPartsComment: trayFromParam.missingPartsComment || '',
            },
            comments: trayFromParam.comments || '',
            status: 'CONFIRMED' as any,
          };
        });

        // 5. Save inspections and run analytics
        if (checkInSet.postCheckinTrayAdd) {
          const inspectionInfo: TrayInspectionInfo = {
            ...allTraysInspection[0],
            ...allTraysInspection[0].trayInspection,
            missingPartsComment: allTraysInspection[0].trayInspection.missingPartsComment,
            stage: 'CHECK_IN',
          };
          await updateTrayInspection({
            trayId: Object.values(checkInSet.trays)[0]?.trayInstanceId || '',
            inspectionInfo,
          });
        } else {
          await saveAllTrayInspections({
            deliveryId,
            setKitInstanceId,
            payload: { skipInspection, allTraysInspection },
          });
          await updateSetInspection({
            deliveryId,
            setId: setKitInstanceId,
            inspection: { status: 'CONFIRMED' },
          });
          toast.success(lang.setCheckInSuccess);
        }

        const { data: updatedDelivery } = await fetchDelivery();
        const trackingPromises: Promise<any>[] = [];

        Object.entries(checkInSet.trays).forEach(([trayId, tray]) => {
          trackingPromises.push(
            trackEvent(EventType.CompleteTrayCheckIn, {
              status: delivery.status,
              order_id: delivery.orderNumber,
              received_by: delivery.receivedDetails?.receivedBy || '',
              set_id: setKitInstanceId,
              tray_instance_id: trayId,
              all_clean: tray.allDevicesCleanOfDebris,
              all_functional: tray.allDevicesFunctional,
              all_parts_present: tray.allDevicesInTray,
              tray_used: null,
              current_user: currentUser.userId,
              tenant_name: currentUser.tenantName,
            }),
          );
        });

        trackingPromises.push(
          trackEvent(EventType.CompleteSetCheckIn, {
            status: delivery.status,
            order_id: delivery.orderNumber,
            received_by: delivery.receivedDetails?.receivedBy || '',
            set_id: setKitInstanceId,
            current_user: currentUser.userId,
            tenant_name: currentUser.tenantName,
          }),
        );

        if (updatedDelivery?.status === 'CHECKED_IN') {
          trackingPromises.push(
            trackEvent(EventType.CompleteDeliveryCheckIn, {
              delivery_id: deliveryId,
              order_id: delivery.orderNumber,
              received_by: delivery.receivedDetails?.receivedBy || '',
              no_road_cases: delivery.shipperDetails?.numRoadCases || null,
              no_boxes: delivery.shipperDetails?.numBoxes || null,
              num_tubs: delivery.shipperDetails?.numTubs || null,
              no_pelican_cases: delivery.shipperDetails?.numPelicanCases || null,
              no_double_road_cases: delivery.shipperDetails?.numDoubleRoadCases || null,
              current_user: currentUser.userId,
              tenant_name: currentUser.tenantName,
            }),
          );
        }

        await Promise.all([
          queryClient.invalidateQueries([QueryKeys.Order, delivery.orderRecordId]),
          queryClient.invalidateQueries([QueryKeys.Orders]),
          ...trackingPromises,
        ]);
      } catch (error) {
        console.error('Failed to save check-in inspections:', error);
        toast.error('An error occurred while saving. Please try again.');
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [
      allCheckInStates,
      constructTrayImagePath,
      currentUser,
      delivery,
      deliveryId,
      fetchDelivery,
      lang.setCheckInSuccess,
      queryClient,
      saveAllTrayInspections,
      setCheckInTraysBySet,
      updateSetInspection,
      updateTrayInspection,
      uploadImage,
    ],
  );

  return {
    mutateAsync: saveCheckInTrayInspectionsForSet,
    isLoading,
  };
};
